/**
 * Edit Page - Edit video metadata
 */

import { createNavigation } from '../components/navigation.js';
import { api } from '../lib/api.js';
import { getLanguage, t } from '../lib/i18n.js';
import { filterLanguages, SUBTITLE_LANGUAGES } from '../lib/languages.js';
import { showConfirm, showModal } from '../lib/modal.js';
import { router } from '../lib/router.js';

export async function renderEdit(params) {
  const app = document.getElementById('app');
  app.innerHTML = '';

  app.appendChild(await createNavigation());

  const videoId = params?.id;

  if (!videoId) {
    app.innerHTML =
      '<div class="container"><div class="card"><p>Video ID is required</p><a href="/manage" class="btn btn-primary" data-link>Back to Manage</a></div></div>';
    return;
  }

  const main = document.createElement('main');
  main.className = 'admin-main';
  main.innerHTML = `
    <div class="container">
      <div class="mb-lg flex flex-between flex-center">
        <div>
          <h1>${t('form_edit_video')}</h1>
          <p style="color: var(--color-cloud-grey); margin-top: 8px;">
            Rætta filmupplýsingar og stillingar
          </p>
        </div>
        <a href="/manage" class="btn btn-secondary" data-link>
          ← Aftur til Umsit
        </a>
      </div>
      
      <div class="card" style="max-width: 900px; margin: 0 auto;">
        <div id="loading-container" style="text-align: center; padding: 40px;">
          <div class="spinner"></div>
          <p style="margin-top: 16px; color: var(--color-cloud-grey);">Lesur film...</p>
        </div>
        <div id="edit-form-container" style="display: none;"></div>
      </div>
    </div>
  `;

  app.appendChild(main);

  // Load video data
  try {
    const videos = await api.getVideos();
    const video = (videos || []).find((v) => v.videoId === videoId);

    if (!video) {
      document.getElementById('loading-container').innerHTML = `
        <p style="color: var(--color-error);">Video not found</p>
        <a href="/manage" class="btn btn-primary mt-md" data-link>Back to Manage</a>
      `;
      return;
    }

    // Render edit form
    renderEditForm(video, videoId);
  } catch (error) {
    console.error('Failed to load video:', error);
    document.getElementById('loading-container').innerHTML = `
      <p style="color: var(--color-error);">Failed to load video: ${error.message}</p>
      <a href="/manage" class="btn btn-primary mt-md" data-link>Back to Manage</a>
    `;
  }

  function renderEditForm(video, videoId) {
    const container = document.getElementById('edit-form-container');
    document.getElementById('loading-container').style.display = 'none';
    container.style.display = 'block';

    // Debug: Log video pricing data on load
    console.log('[EDIT] Video loaded with pricing:', {
      pricingType: video.pricingType,
      pricingTier: video.pricingTier,
      customPrice: video.customPrice,
      rentalPrice: video.rentalPrice,
    });

    // Determine pricingType if not set, based on rentalPrice
    if (!video.pricingType && video.rentalPrice !== undefined) {
      if (video.rentalPrice === 0) {
        video.pricingType = 'free';
      } else if (video.customPrice && video.customPrice === video.rentalPrice) {
        video.pricingType = 'custom';
      } else if (video.pricingTier) {
        video.pricingType = 'standard';
      } else {
        // Has a price but no tier - must be custom
        video.pricingType = 'custom';
        video.customPrice = video.rentalPrice;
      }
    }

    // Helper to normalize role data (supports both old single-object and new array format)
    function normalizeRoleData(data) {
      if (!data) return [{}];
      if (Array.isArray(data)) return data.length > 0 ? data : [{}];
      return [data]; // single object → wrap in array
    }

    // Helper to render role input groups
    function renderRoleInputs(role, data) {
      const people = normalizeRoleData(data);
      return people
        .map(
          (person, i) => `
        <div class="name-input-group ${i > 0 ? 'mt-sm' : ''}">
          <input type="text" id="edit-${role}${i}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${escapeHtml(person.firstName || '')}">
          <input type="text" id="edit-${role}${i}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${escapeHtml(person.middleName || '')}">
          <input type="text" id="edit-${role}${i}LastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${escapeHtml(person.lastName || '')}">
        </div>
      `,
        )
        .join('');
    }

    // Track counts for each role
    const editRoleCounts = {
      director: normalizeRoleData(video.director).length,
      screenwriter: normalizeRoleData(video.screenwriter).length,
      producer: normalizeRoleData(video.producer).length,
      dop: normalizeRoleData(video.dop).length,
      editor: normalizeRoleData(video.editor).length,
      composer: normalizeRoleData(video.composer).length,
    };

    const existingFileSizeBytes = video.fileSizeBytes ?? video.fileSize ?? null;
    const existingFileSizeLabel =
      existingFileSizeBytes != null
        ? formatFileSize(existingFileSizeBytes)
        : null;
    const existingFileExt = video.fileName
      ? video.fileName.split('.').pop().toUpperCase()
      : '';
    const existingFileFormat =
      video.mimeType || video.fileType || existingFileExt || '';

    container.innerHTML = `
      <form id="edit-form">
        <!-- Current Video File Info (read-only) -->
        <div class="form-section">
          <h3 class="form-section-title">Videofíla</h3>
          <div class="flex flex-between flex-center" style="padding: 12px; background: rgba(0, 119, 192, 0.1); border-radius: 8px;">
            <div>
              <strong>${escapeHtml(video.fileName || '—')}</strong>
              <div style="font-size: 14px; color: var(--color-cloud-grey);">
                <span>${existingFileSizeLabel ?? '—'}</span>${
                  existingFileFormat
                    ? ` <span style="margin-left: 8px; opacity: 0.8;">(${escapeHtml(existingFileFormat)})</span>`
                    : ''
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Replace Video File (Collapsible) -->
        <div class="form-section" style="background-color: rgba(245, 166, 35, 0.1); border: 1px solid rgba(245, 166, 35, 0.3); border-radius: 8px;">
          <div style="cursor: pointer; padding: 16px;" onclick="const content = this.parentElement.querySelector('.reupload-content'); const isHidden = content.style.display === 'none'; content.style.display = isHidden ? 'block' : 'none'; this.querySelector('.reupload-toggle').textContent = isHidden ? '▲' : '▼';">
            <h3 class="form-section-title" style="margin: 0; display: flex; justify-content: space-between; align-items: center;">
              <span>🔄 Skift videofílu</span>
              <span class="reupload-toggle" style="font-size: 1.2rem;">▼</span>
            </h3>
            <p style="margin: 8px 0 0 0; color: var(--color-cloud-grey); font-size: 0.875rem;">Trýst her fyri at leggja upp nýggja videofílu (setur encoding í gongd)</p>
          </div>
          <div class="reupload-content" style="display: none; padding: 0 16px 16px 16px;">
            <div style="background-color: rgba(220, 53, 69, 0.1); border: 1px solid rgba(220, 53, 69, 0.3); border-radius: 8px; padding: 12px; margin-bottom: 16px;">
              <p style="margin: 0; color: var(--color-error); font-size: 0.875rem;">
                ⚠️ <strong>Warning:</strong> Uploading a new file will replace the existing video and trigger re-encoding. The old file will be overwritten.
              </p>
            </div>
            <div class="form-group">
              <div class="upload-zone" id="edit-upload-zone">
                <div class="upload-icon">📹</div>
                <div class="upload-text">Trýst fyri at velja ella drag videofílu her</div>
                <input type="file" id="edit-file-input" accept="video/*,.mxf,.MXF" style="display: none;">
              </div>
              <div id="edit-file-info" style="display: none; margin-top: 16px;">
                <div class="flex flex-between flex-center" style="padding: 12px; background: rgba(0, 119, 192, 0.1); border-radius: 8px;">
                  <div>
                    <strong id="edit-file-name"></strong>
                    <div style="font-size: 14px; color: var(--color-cloud-grey);"><span id="edit-file-size"></span> <span id="edit-file-format" style="margin-left: 8px; opacity: 0.8;"></span></div>
                  </div>
                  <button type="button" class="btn btn-small btn-danger" id="edit-remove-file">Strika</button>
                </div>
              </div>
            </div>
            
            <!-- Upload Progress -->
            <div id="edit-upload-progress" style="display: none;">
              <div class="form-label">Leggur upp...</div>
              <div class="progress-bar">
                <div class="progress-fill" id="edit-progress-fill" style="width: 0%"></div>
              </div>
              <div style="text-align: center; margin-top: 8px; color: var(--color-cloud-grey);">
                <span id="edit-progress-text">0%</span>
              </div>
            </div>
            
            <button type="button" class="btn btn-warning" id="upload-replacement-btn" style="margin-top: 16px; display: none;">
              🔄 Legg upp og set encoding í gongd
            </button>
          </div>
        </div>
        
        <!-- Category -->
        <div class="form-section">
          <h3 class="form-section-title">${t('form_category')} *</h3>
          <div class="form-group">
            <select id="edit-category" class="form-input">
              <option value="">${t('form_category')}...</option>
              <option value="short" ${video.category === 'short' ? 'selected' : ''}>${t('category_short')}</option>
              <option value="feature" ${video.category === 'feature' ? 'selected' : ''}>${t('category_feature')}</option>
              <option value="documentary" ${video.category === 'documentary' ? 'selected' : ''}>${t('category_documentary')}</option>
              <option value="documentary_short" ${video.category === 'documentary_short' ? 'selected' : ''}>${t('category_documentary_short')}</option>
              <option value="documentary_long" ${video.category === 'documentary_long' ? 'selected' : ''}>${t('category_documentary_long')}</option>
            </select>
          </div>
        </div>

        <!-- Age Rating -->
        <div class="form-section">
          <h3 class="form-section-title">Aldursmark</h3>
          <div class="form-group">
            <select id="edit-ageRating" class="form-input">
              <option value="">Einki valt</option>
              <option value="A" ${video.ageRating === 'A' ? 'selected' : ''}>A (Allir)</option>
              <option value="7" ${video.ageRating === '7' ? 'selected' : ''}>7</option>
              <option value="11" ${video.ageRating === '11' ? 'selected' : ''}>11</option>
              <option value="15" ${video.ageRating === '15' ? 'selected' : ''}>15</option>
            </select>
            <p class="form-help">Danskt/nordiskt aldursmarkering (Medierådet)</p>
          </div>
        </div>

        <!-- Basic Information -->
        <div class="form-section">
          <h3 class="form-section-title">${t('form_section_basic')}</h3>

          <div class="form-group">
            <label class="form-label">${t('form_original_title')} *</label>
            <input type="text" id="edit-originalTitle" class="form-input" value="${escapeHtml(video.originalTitle || video.title || '')}" required>
          </div>
          
          <div class="form-group">
            <label class="form-label">${t('form_international_title')}</label>
            <input type="text" id="edit-internationalTitle" class="form-input" value="${escapeHtml(video.internationalTitle || '')}">
          </div>
          
          <div class="form-group">
            <label class="form-label">Stuttur søgusamandráttur / Logline (Faroese) 🇫🇴 <small style="color: var(--color-cloud-grey);">(í mesta lagi 200 tekin)</small></label>
            <textarea id="edit-loglineFo" class="form-textarea" rows="2" maxlength="200">${escapeHtml(video.loglineFo || '')}</textarea>
            <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-loglineFo-count">${(video.loglineFo || '').length}</span>/200 tekin</small>
          </div>
          
          <div class="form-group">
            <label class="form-label">Um filmin / Synopsis (Faroese) 🇫🇴 <small style="color: var(--color-cloud-grey);">(í mesta lagi 600 tekin)</small></label>
            <textarea id="edit-synopsisFo" class="form-textarea" rows="4" maxlength="600">${escapeHtml(video.synopsisFo || '')}</textarea>
            <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-synopsisFo-count">${(video.synopsisFo || '').length}</span>/600 tekin</small>
          </div>
          
          <div class="form-group">
            <label class="form-label">Logline (English) 🇬🇧 <small style="color: var(--color-cloud-grey);">(í mesta lagi 200 tekin)</small></label>
            <textarea id="edit-loglineEn" class="form-textarea" rows="2" maxlength="200">${escapeHtml(video.loglineEn || '')}</textarea>
            <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-loglineEn-count">${(video.loglineEn || '').length}</span>/200 tekin</small>
          </div>
          
          <div class="form-group">
            <label class="form-label">Synopsis (English) 🇬🇧 <small style="color: var(--color-cloud-grey);">(í mesta lagi 600 tekin)</small></label>
            <textarea id="edit-synopsisEn" class="form-textarea" rows="4" maxlength="600">${escapeHtml(video.synopsisEn || '')}</textarea>
            <small style="color: var(--color-cloud-grey); font-size: 0.875rem;"><span id="edit-synopsisEn-count">${(video.synopsisEn || '').length}</span>/600 tekin</small>
          </div>
          
          <!-- Poster Image -->
          <div class="form-group">
            <label class="form-label">${t('form_poster_url')}</label>
            <div style="display: flex; gap: 12px; flex-direction: column;">
              <div style="display: flex; gap: 12px; align-items: center;">
                <input type="file" id="edit-posterFile" accept="image/jpeg,image/png,image/webp" style="display: none;">
                <button type="button" class="btn btn-secondary btn-small" id="edit-selectPosterBtn">📁 ${t('upload_select_file')}</button>
                <span id="edit-posterFileName" style="color: var(--color-cloud-grey); font-size: 0.875rem;"></span>
              </div>
              <div style="text-align: center; color: var(--color-cloud-grey); font-size: 0.875rem;">ella</div>
              <!-- type="text" instead of "url": the existing posterUrl is the API's
                   relative path /images/asset/<id>, which fails HTML5 URL validation
                   and blocks submit until the user clears it by uploading a new image. -->
              <input type="text" id="edit-posterUrl" class="form-input" placeholder="${t('placeholder_poster_url')}" value="${escapeHtml(video.posterUrl || '')}">
            </div>
            <p class="form-help">Legg upp fílu ella set inn URL • Tilmælt: 640x960px (2:3 standandi)</p>
          </div>
          
          <!-- Still Image -->
          <div class="form-group">
            <label class="form-label">${t('form_still_url')}</label>
            <div style="display: flex; gap: 12px; flex-direction: column;">
              <div style="display: flex; gap: 12px; align-items: center;">
                <input type="file" id="edit-stillFile" accept="image/jpeg,image/png,image/webp" style="display: none;">
                <button type="button" class="btn btn-secondary btn-small" id="edit-selectStillBtn">📁 ${t('upload_select_file')}</button>
                <span id="edit-stillFileName" style="color: var(--color-cloud-grey); font-size: 0.875rem;"></span>
              </div>
              <div style="text-align: center; color: var(--color-cloud-grey); font-size: 0.875rem;">ella</div>
              <input type="text" id="edit-stillUrl" class="form-input" placeholder="${t('placeholder_still_url')}" value="${escapeHtml(video.stillUrl || '')}">
            </div>
            <p class="form-help">Legg upp fílu ella set inn URL • Tilmælt: 1920x1080px (16:9 liggjandi)</p>
          </div>
        </div>
        
        <!-- Subtitles -->
        <div class="form-section">
          <h3 class="form-section-title">Undirtekstir</h3>
          <div id="edit-subtitle-list" style="margin-bottom: var(--spacing-md);"></div>
          <button type="button" class="btn btn-secondary" id="edit-add-subtitle-btn">+ Legg afturat undirtekstir</button>

          <div class="form-group" style="margin-top: var(--spacing-md);">
            <label class="form-label">${t('form_subtitles')}</label>
            <input type="text" id="edit-subtitles" class="form-input" value="${escapeHtml(video.subtitles || '')}">
          </div>
        </div>
        
        <!-- Crew -->
        <div class="form-section">
          <h3 class="form-section-title">${t('form_section_crew')}</h3>
          
          <!-- Director -->
          <div class="form-group">
            <label class="form-label">${t('form_director')}</label>
            <div id="edit-director-container">
              ${renderRoleInputs('director', video.director)}
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm edit-add-role-btn" data-role="director">+ Legg aftrat</button>
          </div>

          <!-- Screenwriter -->
          <div class="form-group">
            <label class="form-label">${t('form_screenwriter')}</label>
            <div id="edit-screenwriter-container">
              ${renderRoleInputs('screenwriter', video.screenwriter)}
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm edit-add-role-btn" data-role="screenwriter">+ Legg aftrat</button>
          </div>

          <!-- Producer -->
          <div class="form-group">
            <label class="form-label">${t('form_producer')}</label>
            <div id="edit-producer-container">
              ${renderRoleInputs('producer', video.producer)}
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm edit-add-role-btn" data-role="producer">+ Legg aftrat</button>
          </div>

          <!-- Director of Photography -->
          <div class="form-group">
            <label class="form-label">${t('form_dop')}</label>
            <div id="edit-dop-container">
              ${renderRoleInputs('dop', video.dop)}
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm edit-add-role-btn" data-role="dop">+ Legg aftrat</button>
          </div>

          <!-- Editor -->
          <div class="form-group">
            <label class="form-label">${t('form_editor')}</label>
            <div id="edit-editor-container">
              ${renderRoleInputs('editor', video.editor)}
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm edit-add-role-btn" data-role="editor">+ Legg aftrat</button>
          </div>

          <!-- Music Composer -->
          <div class="form-group">
            <label class="form-label">${t('form_composer')}</label>
            <div id="edit-composer-container">
              ${renderRoleInputs('composer', video.composer)}
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm edit-add-role-btn" data-role="composer">+ Legg aftrat</button>
          </div>
          
          <!-- Main Cast -->
          <div class="form-group">
            <label class="form-label">${t('form_main_cast')}</label>
            <textarea id="edit-mainCast" class="form-textarea" rows="2" placeholder="First Middle Last, First Middle Last, ...">${(video.mainCast || []).map((c) => escapeHtml(`${c.firstName || ''} ${c.middleName || ''} ${c.lastName || ''}`.trim())).join(', ')}</textarea>
            <small style="color: var(--color-cloud-grey); font-size: 0.875rem;">Skil leikarar at við komma</small>
          </div>
          
          <!-- Key Crew -->
          <div class="form-group">
            <label class="form-label">${t('form_key_crew')}</label>
            <div id="edit-crew-container">
              ${
                (video.keyCrew || []).length > 0
                  ? (video.keyCrew || [])
                      .map(
                        (c, i) => `
                <div class="name-input-group ${i > 0 ? 'mt-sm' : ''}">
                  <select id="edit-crew${i}Role" class="form-input" style="min-width: 200px;">
                    <option value="">Vel leiklut...</option>
                    <option value="production_designer" ${c.role === 'production_designer' ? 'selected' : ''}>Production Designer / Framleiðslusniðgevi</option>
                    <option value="costume_designer" ${c.role === 'costume_designer' ? 'selected' : ''}>Costume Designer / Búnasniðgevi</option>
                    <option value="makeup_artist" ${c.role === 'makeup_artist' ? 'selected' : ''}>Makeup Artist / Sminkulistafólk</option>
                    <option value="sound_recordist" ${c.role === 'sound_recordist' ? 'selected' : ''}>Sound Recordist / Ljóðmeistari</option>
                    <option value="sound_designer" ${c.role === 'sound_designer' ? 'selected' : ''}>Sound Designer / Ljóðsniðgevi</option>
                    <option value="gaffer" ${c.role === 'gaffer' ? 'selected' : ''}>Gaffer / Ljósmeistari</option>
                    <option value="key_grip" ${c.role === 'key_grip' ? 'selected' : ''}>Key Grip / Høvuðsgrippur</option>
                    <option value="colourist" ${c.role === 'colourist' ? 'selected' : ''}>Colourist / Litviðgeri</option>
                  </select>
                  <input type="text" id="edit-crew${i}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}" value="${escapeHtml(c.firstName || '')}">
                  <input type="text" id="edit-crew${i}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}" value="${escapeHtml(c.middleName || '')}">
                  <input type="text" id="edit-crew${i}LastName" class="form-input" placeholder="${t('placeholder_lastname')}" value="${escapeHtml(c.lastName || '')}">
                </div>
              `,
                      )
                      .join('')
                  : `
                <div class="name-input-group">
                  <select id="edit-crew0Role" class="form-input" style="min-width: 200px;">
                    <option value="">Vel leiklut...</option>
                    <option value="production_designer">Production Designer / Framleiðslusniðgevi</option>
                    <option value="costume_designer">Costume Designer / Búnasniðgevi</option>
                    <option value="makeup_artist">Makeup Artist / Sminkulistafólk</option>
                    <option value="sound_recordist">Sound Recordist / Ljóðmeistari</option>
                    <option value="sound_designer">Sound Designer / Ljóðsniðgevi</option>
                    <option value="gaffer">Gaffer / Ljósmeistari</option>
                    <option value="key_grip">Key Grip / Høvuðsgrippur</option>
                    <option value="colourist">Colourist / Litviðgeri</option>
                  </select>
                  <input type="text" id="edit-crew0FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
                  <input type="text" id="edit-crew0MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
                  <input type="text" id="edit-crew0LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
                </div>
              `
              }
            </div>
            <button type="button" class="btn btn-secondary btn-small mt-sm" id="edit-add-crew">+ Legg aftrat liðsmann</button>
          </div>
        </div>
        
        <!-- Screenings & Awards -->
        <div class="form-section">
          <h3 class="form-section-title">Sýningar & virðislønir</h3>
          <p class="form-help" style="margin-bottom: var(--spacing-md);">Legg aftrat filmsfestivalar, sýningar og virðislønir (í mesta lagi 6 skrásetingar)</p>
          <div id="edit-screenings-container"></div>
          <button type="button" class="btn btn-secondary btn-small mt-sm" id="edit-add-screening">+ Legg aftrat sýning</button>
        </div>

        <!-- Production Companies -->
        <div class="form-section">
          <h3 class="form-section-title">Framleiðsla</h3>

          <div class="form-group">
            <label class="form-label">${t('form_production_company')}</label>
            <input type="text" id="edit-productionCompany" class="form-input" value="${escapeHtml(video.productionCompany || '')}">
          </div>

          <div class="form-group">
            <label class="form-label">${t('form_coproduction_company')}</label>
            <input type="text" id="edit-coproductionCompany" class="form-input" value="${escapeHtml(video.coproductionCompany || '')}">
          </div>
        </div>
        
        <!-- Technical Details -->
        <div class="form-section">
          <h3 class="form-section-title">${t('form_section_technical')}</h3>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px;">
            <div class="form-group">
              <label class="form-label">${t('form_runtime_hours')}</label>
              <input type="number" id="edit-runtimeHours" class="form-input" min="0" max="10" value="${video.runtimeHours || 0}">
            </div>
            
            <div class="form-group">
              <label class="form-label">${t('form_runtime_minutes')}</label>
              <input type="number" id="edit-runtimeMinutes" class="form-input" min="0" max="59" value="${video.runtimeMinutes || 0}">
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">${t('form_language')}</label>
            <input type="text" id="edit-language" class="form-input" value="${escapeHtml(video.language || '')}">
          </div>
          
          <div class="form-group">
            <label class="form-label">${t('form_production_year')}</label>
            <input type="number" id="edit-productionYear" class="form-input" min="1900" max="2100" value="${video.productionYear || video.year || ''}">
          </div>
          
          <div class="form-group">
            <label class="form-label">${t('form_imdb_link')}</label>
            <input type="url" id="edit-imdbLink" class="form-input" value="${escapeHtml(video.imdbLink || '')}">
          </div>
        </div>
        
        <!-- Pricing / Cost -->
        <div class="form-section">
          <h3 class="form-section-title">KOSTNAÐUR *</h3>

          <div class="form-group">
            <label class="form-label">Prísseting *</label>
            <select id="edit-pricingType" class="form-input" required>
              <option value="standard" ${(video.pricingType || 'standard') === 'standard' ? 'selected' : ''}>Vanlig prísseting</option>
              <option value="free" ${video.pricingType === 'free' ? 'selected' : ''}>Ókeypis</option>
              <option value="custom" ${video.pricingType === 'custom' ? 'selected' : ''}>Onnur upphædd</option>
            </select>
          </div>
          
          <div class="form-group" id="edit-pricing-tier-group" style="display: ${(video.pricingType || 'standard') === 'standard' ? 'block' : 'none'};">
            <label class="form-label">Prísflokkur</label>
            <select id="edit-pricingTier" class="form-input">
              <option value="">Vel prísfokk...</option>
            </select>
          </div>
          
          <div class="form-group" id="edit-custom-price-group" style="display: ${video.pricingType === 'custom' ? 'block' : 'none'};">
            <label class="form-label">Annar prísurin (kr.)</label>
            <input type="number" id="edit-customPrice" class="form-input" min="0" step="0.01" value="${video.customPrice ? (video.customPrice / 100).toFixed(2) : video.rentalPrice && video.rentalPrice > 0 ? (video.rentalPrice / 100).toFixed(2) : ''}" placeholder="0.00">
            <p class="form-help">Skriva prís í kr. (t.d. 29.00)</p>
          </div>
          
          <div class="form-group" id="edit-rental-duration-group" style="display: none;">
            <label class="form-label">Leigunarskeið (tímar)</label>
            <input type="number" id="edit-rentalDuration" class="form-input" min="1" max="168" value="${video.rentalDurationHours || 48}">
            <small style="color: var(--color-cloud-grey); font-size: 0.875rem;">Hvussu leingi brúkarar kunnu hyggja (standard: 48t)</small>
          </div>
        </div>
        
        <!-- Bank Information -->
        <div class="form-section" id="edit-bank-info-section" style="display: ${video.pricingType === 'free' ? 'none' : 'block'};">
          <h3 class="form-section-title">BANKAUPPLÝSINGAR</h3>

          <div class="form-group">
            <label class="form-label" for="edit-bankName">Banki *</label>
            <input type="text" id="edit-bankName" class="form-input" value="${escapeHtml(video.bankName || '')}">
          </div>

          <div class="form-group">
            <label class="form-label" for="edit-bankRegistrationNo">Skrás. nr.</label>
            <input type="text" id="edit-bankRegistrationNo" class="form-input" value="${escapeHtml(video.bankRegistrationNo || '')}">
          </div>

          <div class="form-group">
            <label class="form-label" for="edit-bankAccountNo">Konto nr.</label>
            <input type="text" id="edit-bankAccountNo" class="form-input" value="${escapeHtml(video.bankAccountNo || '')}">
          </div>
        </div>

        ${renderRightsTermsAudit(video)}
        
        <!-- Distribution -->
        <div class="form-section">
          <h3 class="form-section-title">DISTRIBUTION</h3>

          <div class="form-group">
            <label class="checkbox-label" style="display: flex; align-items: flex-start; gap: var(--spacing-sm); cursor: pointer;">
              <input type="checkbox" id="edit-geoRestricted" ${video.geoRestricted ? 'checked' : ''} style="margin-top: 4px; flex-shrink: 0; width: 20px; height: 20px; cursor: pointer;">
              <span>Avmarka til Føroyar (FO)</span>
            </label>
            <p class="form-help">Tá ið hesin er virkin, kann filmurin bert síggjast í Føroyum.</p>
          </div>
        </div>

        <div class="flex flex-gap-md mt-md" style="padding-top: 2rem; border-top: 1px solid rgba(255, 255, 255, 0.1);">
          <button type="submit" class="btn btn-primary">${t('action_save')}</button>
          <a href="/manage" class="btn btn-secondary" data-link>${t('action_cancel')}</a>
        </div>
      </form>
    `;

    // Setup form handlers
    setupEditFormHandlers(video, videoId, editRoleCounts);
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatAuditDate(value) {
    if (!value) return t('rights_terms_audit_missing');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString(getLanguage() === 'fo' ? 'fo-FO' : 'en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  function renderAuditValue(value) {
    if (value === null || value === undefined || value === '') {
      return `<span class="audit-muted">${t('rights_terms_audit_missing')}</span>`;
    }

    return escapeHtml(String(value));
  }

  function renderAuditField(label, value, extraClass = '') {
    return `
      <div class="audit-field ${extraClass}">
        <span>${label}</span>
        <strong>${value}</strong>
      </div>
    `;
  }

  function renderRightsTermsAudit(video) {
    const accepted = Boolean(video.rightsTermsAccepted);
    const statusClass = accepted ? 'status-completed' : 'status-pending';
    const statusText = accepted
      ? t('rights_terms_audit_accepted')
      : t('rights_terms_audit_missing');

    return `
      <div class="form-section">
        <h3 class="form-section-title">${t('rights_terms_audit_title')}</h3>
        <div class="rights-audit-panel">
          <div class="rights-audit-status">
            <span class="status-badge ${statusClass}">${statusText}</span>
          </div>
          <div class="audit-grid">
            ${renderAuditField(t('rights_terms_audit_version'), renderAuditValue(video.rightsTermsVersion))}
            ${renderAuditField(t('rights_terms_audit_date'), accepted ? escapeHtml(formatAuditDate(video.rightsTermsAcceptedAt)) : renderAuditValue(null))}
            ${renderAuditField(t('rights_terms_audit_user'), renderAuditValue(video.rightsTermsAcceptedByEmail || video.rightsTermsAcceptedByUserId))}
            ${renderAuditField(t('rights_terms_audit_language'), renderAuditValue(video.rightsTermsLanguage))}
            ${renderAuditField(t('rights_terms_audit_ip'), renderAuditValue(video.rightsTermsAcceptanceIp))}
            ${renderAuditField(t('rights_terms_audit_user_agent'), renderAuditValue(video.rightsTermsAcceptanceUserAgent), 'audit-field-wide')}
          </div>
        </div>
      </div>
    `;
  }

  function setupEditFormHandlers(video, videoId, editRoleCounts) {
    // Video file replacement handlers
    let selectedReplacementFile = null;

    const editUploadZone = document.getElementById('edit-upload-zone');
    const editFileInput = document.getElementById('edit-file-input');
    const editFileInfo = document.getElementById('edit-file-info');
    const editFileName = document.getElementById('edit-file-name');
    const editFileSize = document.getElementById('edit-file-size');
    const editRemoveFileBtn = document.getElementById('edit-remove-file');
    const uploadReplacementBtn = document.getElementById(
      'upload-replacement-btn',
    );
    const editProgressContainer = document.getElementById(
      'edit-upload-progress',
    );
    const editProgressFill = document.getElementById('edit-progress-fill');
    const editProgressText = document.getElementById('edit-progress-text');

    // File upload handlers
    editUploadZone.addEventListener('click', () => editFileInput.click());

    editUploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      editUploadZone.style.borderColor = 'var(--primary-color)';
      editUploadZone.style.background = 'rgba(0, 119, 192, 0.05)';
    });

    editUploadZone.addEventListener('dragleave', () => {
      editUploadZone.style.borderColor = 'rgba(255, 255, 255, 0.1)';
      editUploadZone.style.background = 'rgba(255, 255, 255, 0.02)';
    });

    editUploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      editUploadZone.style.borderColor = 'rgba(255, 255, 255, 0.1)';
      editUploadZone.style.background = 'rgba(255, 255, 255, 0.02)';

      if (e.dataTransfer.files.length > 0) {
        handleReplacementFileSelect(e.dataTransfer.files[0]);
      }
    });

    editFileInput.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        handleReplacementFileSelect(e.target.files[0]);
      }
    });

    editRemoveFileBtn.addEventListener('click', () => {
      selectedReplacementFile = null;
      editFileInput.value = '';
      editFileInfo.style.display = 'none';
      editUploadZone.style.display = 'flex';
      uploadReplacementBtn.style.display = 'none';
    });

    function handleReplacementFileSelect(file) {
      selectedReplacementFile = file;
      editFileName.textContent = file.name;
      editFileSize.textContent = formatFileSize(file.size);
      const editFileFormat = document.getElementById('edit-file-format');
      if (editFileFormat) {
        const ext = file.name.split('.').pop().toUpperCase();
        editFileFormat.textContent = `(${file.type || ext})`;
      }
      editFileInfo.style.display = 'block';
      editUploadZone.style.display = 'none';
      uploadReplacementBtn.style.display = 'block';
    }

    function formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
      if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    }

    // Upload replacement video
    // Track replacement upload state
    let isReplacementUploading = false;

    // Prevent navigation during replacement upload
    const handleBeforeUnload = (e) => {
      if (isReplacementUploading) {
        e.preventDefault();
        e.returnValue =
          'Video replacement upload in progress. If you leave, the upload will be paused and you can resume it later.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    uploadReplacementBtn.addEventListener('click', async () => {
      if (!selectedReplacementFile) return;

      const fileSizeMB = (selectedReplacementFile.size / (1024 * 1024)).toFixed(
        2,
      );

      const confirmed = await showConfirm(
        `⚠️ Replace video file for "${video.originalTitle || video.title}"?\n\n` +
          `File: ${selectedReplacementFile.name}\n` +
          `Size: ${fileSizeMB} MB\n\n` +
          `This will overwrite the existing file and trigger re-encoding.\n\n` +
          `Important: Stay on this page during upload. If you navigate away, the upload will pause and you can resume it later.`,
        'Replace Video File',
      );

      if (!confirmed) return;

      try {
        isReplacementUploading = true;
        uploadReplacementBtn.disabled = true;
        uploadReplacementBtn.textContent = 'Leggur upp...';
        editProgressContainer.style.display = 'block';

        // Replace video file and trigger re-encoding
        await api.replaceVideo(
          selectedReplacementFile,
          videoId, // Use existing videoId to replace
          (progress) => {
            editProgressFill.style.width = `${progress}%`;
            editProgressText.textContent = `${progress}%`;
          },
        );

        // Upload complete
        isReplacementUploading = false;

        await showModal(
          '✅ Video file replaced successfully!\n\n' +
            'Re-encoding has been triggered. You can now safely navigate away.',
          'success',
          'Replacement Complete',
        );
        router.navigate('/manage');
      } catch (error) {
        console.error('Failed to replace video:', error);
        isReplacementUploading = false;
        await showModal(error.message, 'error', 'Uppløga miseydnaðist');
        uploadReplacementBtn.disabled = false;
        uploadReplacementBtn.textContent = '🔄 Upload & Trigger Re-encoding';
        editProgressContainer.style.display = 'none';
      }
    });

    // Image file upload handlers
    let selectedPosterFile = null;
    let selectedStillFile = null;

    const posterFileInput = document.getElementById('edit-posterFile');
    const stillFileInput = document.getElementById('edit-stillFile');
    const selectPosterBtn = document.getElementById('edit-selectPosterBtn');
    const selectStillBtn = document.getElementById('edit-selectStillBtn');
    const posterFileName = document.getElementById('edit-posterFileName');
    const stillFileName = document.getElementById('edit-stillFileName');

    selectPosterBtn.addEventListener('click', () => posterFileInput.click());
    selectStillBtn.addEventListener('click', () => stillFileInput.click());

    posterFileInput.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        selectedPosterFile = e.target.files[0];
        posterFileName.textContent = `${selectedPosterFile.name} (${formatFileSize(selectedPosterFile.size)}, ${selectedPosterFile.type || 'unknown'})`;
        document.getElementById('edit-posterUrl').value = '';
      }
    });

    stillFileInput.addEventListener('change', (e) => {
      if (e.target.files.length > 0) {
        selectedStillFile = e.target.files[0];
        stillFileName.textContent = `${selectedStillFile.name} (${formatFileSize(selectedStillFile.size)}, ${selectedStillFile.type || 'unknown'})`;
        document.getElementById('edit-stillUrl').value = '';
      }
    });

    // Subtitle management
    const subtitleList = document.getElementById('edit-subtitle-list');
    const addSubtitleBtn = document.getElementById('edit-add-subtitle-btn');
    let existingSubtitles = [];
    let subtitlesToUpload = [];

    async function loadSubtitles() {
      try {
        existingSubtitles = await api.getSubtitles(videoId);
        renderSubtitleList();
      } catch (error) {
        console.error('Failed to load subtitles:', error);
      }
    }

    function renderSubtitleList() {
      const allSubtitles = [
        ...existingSubtitles,
        ...subtitlesToUpload.map((s) => ({ ...s, isNew: true })),
      ];

      if (allSubtitles.length === 0) {
        subtitleList.innerHTML =
          '<p style="color: var(--color-cloud-grey); font-size: 0.875rem; margin: 0;">Ongir undirtekstir enn</p>';
        return;
      }

      subtitleList.innerHTML = allSubtitles
        .map((subtitle, index) => {
          const isNew = subtitle.isNew;
          const subtitleId = subtitle.id || `new-${index}`;
          return `
          <div class="subtitle-item" data-id="${subtitleId}">
            <div>
              <strong>${subtitle.label}</strong> (${subtitle.language})
              ${isNew ? '<span style="color: var(--color-sky-blue); font-size: 0.75rem; margin-left: 8px;">(new)</span>' : ''}
              ${!isNew ? `<div style="font-size: 0.875rem; color: var(--color-cloud-grey);">Existing subtitle</div>` : `<div style="font-size: 0.875rem; color: var(--color-cloud-grey);">${subtitle.file.name}</div>`}
            </div>
            <button type="button" class="btn btn-small btn-danger" onclick="removeSubtitle('${subtitleId}')">Strika</button>
          </div>
        `;
        })
        .join('');
    }

    window.removeSubtitle = async (subtitleId) => {
      if (subtitleId.startsWith('new-')) {
        // Remove from new uploads
        const index = parseInt(subtitleId.replace('new-', ''), 10);
        subtitlesToUpload = subtitlesToUpload.filter((_, i) => i !== index);
      } else {
        // Delete existing subtitle
        const confirmed = await showConfirm(
          'Ert tú vís/ur í, at tú vilt strika hendan undirtekst?',
          'Strika undirtekst',
          'warning',
        );
        if (!confirmed) return;

        try {
          await api.deleteSubtitle(subtitleId);
          existingSubtitles = existingSubtitles.filter(
            (s) => s.id !== subtitleId,
          );
        } catch (error) {
          await showModal(
            `Broytt at strika undirtekst:\n\n${error.message}`,
            'error',
            'Brek',
          );
          return;
        }
      }
      renderSubtitleList();
    };

    addSubtitleBtn.addEventListener('click', async () => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.srt,.vtt,.ass,.ssa';
      fileInput.style.display = 'none';
      document.body.appendChild(fileInput);

      fileInput.addEventListener('change', async (e) => {
        const file = e.target.files[0];
        if (!file) {
          document.body.removeChild(fileInput);
          return;
        }

        const languageCode = await showLanguageSelector();
        if (!languageCode) {
          document.body.removeChild(fileInput);
          return;
        }

        const language = SUBTITLE_LANGUAGES.find(
          (l) => l.code === languageCode,
        );
        if (!language) {
          await showModal('Ógilt mál valt', 'error', 'Brek');
          document.body.removeChild(fileInput);
          return;
        }

        subtitlesToUpload.push({
          file,
          language: language.code,
          label: language.label,
        });

        renderSubtitleList();
        document.body.removeChild(fileInput);
      });

      fileInput.click();
    });

    function showLanguageSelector() {
      return new Promise((resolve) => {
        const modal = document.createElement('div');
        modal.className = 'custom-modal-overlay';

        const closeModal = (result) => {
          modal.remove();
          resolve(result);
        };

        modal.innerHTML = `
          <div class="modal" style="max-width: 500px;">
            <div class="modal-header">
              <h2>Vel mál</h2>
              <button class="modal-close">×</button>
            </div>
            <div class="modal-content">
              <input type="text" id="language-search" class="form-input" placeholder="Leita eftir málum..." style="margin-bottom: var(--spacing-sm);">
              <div id="language-list" style="max-height: 300px; overflow-y: auto;">
                ${SUBTITLE_LANGUAGES.map(
                  (lang) => `
                  <div class="language-option" data-code="${lang.code}" style="padding: var(--spacing-sm); cursor: pointer; border-radius: var(--radius-sm); margin-bottom: 4px; background: rgba(13, 17, 23, 0.4);">
                    <strong>${lang.code}</strong> - ${lang.label}
                  </div>
                `,
                ).join('')}
              </div>
            </div>
          </div>
        `;

        document.body.appendChild(modal);

        modal
          .querySelector('.modal-close')
          .addEventListener('click', () => closeModal(null));
        modal.addEventListener('click', (e) => {
          if (e.target === modal) closeModal(null);
        });

        const searchInput = modal.querySelector('#language-search');
        const languageList = modal.querySelector('#language-list');

        languageList.addEventListener('click', (e) => {
          const option = e.target.closest('.language-option');
          if (option) {
            closeModal(option.dataset.code);
          }
        });

        searchInput.addEventListener('input', (e) => {
          const term = e.target.value.toLowerCase();
          const filtered = filterLanguages(term);
          languageList.innerHTML = filtered
            .map(
              (lang) => `
            <div class="language-option" data-code="${lang.code}" style="padding: var(--spacing-sm); cursor: pointer; border-radius: var(--radius-sm); margin-bottom: 4px; background: rgba(13, 17, 23, 0.4);">
              <strong>${lang.code}</strong> - ${lang.label}
            </div>
          `,
            )
            .join('');
        });

        searchInput.focus();
      });
    }

    // Load existing subtitles
    loadSubtitles();

    // Character counters
    const editLoglineFo = document.getElementById('edit-loglineFo');
    const editLoglineEn = document.getElementById('edit-loglineEn');
    const editSynopsisFo = document.getElementById('edit-synopsisFo');
    const editSynopsisEn = document.getElementById('edit-synopsisEn');

    [editLoglineFo, editLoglineEn, editSynopsisFo, editSynopsisEn].forEach(
      (textarea) => {
        if (textarea) {
          const counterId = `${textarea.id}-count`;
          const counter = document.getElementById(counterId);
          textarea.addEventListener('input', () => {
            if (counter) counter.textContent = textarea.value.length;
          });
        }
      },
    );

    // Pricing type change handler
    const editPricingTypeSelect = document.getElementById('edit-pricingType');
    const editPricingTierGroup = document.getElementById(
      'edit-pricing-tier-group',
    );
    const editPricingTierSelect = document.getElementById('edit-pricingTier');
    const editCustomPriceGroup = document.getElementById(
      'edit-custom-price-group',
    );
    const editRentalDurationGroup = document.getElementById(
      'edit-rental-duration-group',
    );
    const editBankInfoSection = document.getElementById(
      'edit-bank-info-section',
    );
    const _editBankNameInput = document.getElementById('edit-bankName');
    const _editBankRegistrationNoInput = document.getElementById(
      'edit-bankRegistrationNo',
    );
    const _editBankAccountNoInput =
      document.getElementById('edit-bankAccountNo');
    const editCategorySelect = document.getElementById('edit-category');

    function updateEditPricingFields() {
      const pricingType = editPricingTypeSelect.value;
      const category = editCategorySelect.value;

      if (pricingType === 'free') {
        editPricingTierGroup.style.display = 'none';
        editCustomPriceGroup.style.display = 'none';
        editRentalDurationGroup.style.display = 'none';
        editBankInfoSection.style.display = 'none';
      } else {
        // Rental duration is always hidden (fixed at 48 hours)
        editRentalDurationGroup.style.display = 'none';
        // Show bank info for paid content
        editBankInfoSection.style.display = 'block';

        if (pricingType === 'custom') {
          editPricingTierGroup.style.display = 'none';
          editCustomPriceGroup.style.display = 'block';
        } else if (pricingType === 'standard') {
          editCustomPriceGroup.style.display = 'none';
          editPricingTierGroup.style.display = 'block';

          editPricingTierSelect.innerHTML =
            '<option value="">Vel prísfokk...</option>';

          if (category === 'short') {
            editPricingTierSelect.innerHTML += `
              <option value="short_under_3mo" ${video.pricingTier === 'short_under_3mo' ? 'selected' : ''}>Yngri enn 3 mðr.: 29 kr</option>
              <option value="short_under_1yr" ${video.pricingTier === 'short_under_1yr' ? 'selected' : ''}>Yngri enn 1 ár: 19 kr</option>
              <option value="short_under_4yr" ${video.pricingTier === 'short_under_4yr' ? 'selected' : ''}>Yngri enn 4 ár: 7 kr</option>
              <option value="short_over_4yr" ${video.pricingTier === 'short_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár: 0 kr</option>
            `;
          } else if (category === 'feature') {
            editPricingTierSelect.innerHTML += `
              <option value="feature_under_2yr" ${video.pricingTier === 'feature_under_2yr' ? 'selected' : ''}>Yngri enn 2 ár: 59 kr</option>
              <option value="feature_over_2yr" ${video.pricingTier === 'feature_over_2yr' ? 'selected' : ''}>Eldri enn 2 ár: 39 kr</option>
              <option value="feature_over_4yr" ${video.pricingTier === 'feature_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár: 0 kr</option>
            `;
          } else if (category === 'documentary_short') {
            editPricingTierSelect.innerHTML += `
              <option value="doc_short_under_1yr" ${video.pricingTier === 'doc_short_under_1yr' ? 'selected' : ''}>Yngri enn 1 ár: 19 kr</option>
              <option value="doc_short_under_4yr" ${video.pricingTier === 'doc_short_under_4yr' ? 'selected' : ''}>Yngri enn 4 ár: 7 kr</option>
              <option value="doc_short_over_4yr" ${video.pricingTier === 'doc_short_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár: 0 kr</option>
            `;
          } else if (category === 'documentary_long') {
            editPricingTierSelect.innerHTML += `
              <option value="doc_long_under_2yr" ${video.pricingTier === 'doc_long_under_2yr' ? 'selected' : ''}>Yngri enn 2 ár: 59 kr</option>
              <option value="doc_long_under_4yr" ${video.pricingTier === 'doc_long_under_4yr' ? 'selected' : ''}>Yngri enn 4 ár: 39 kr</option>
              <option value="doc_long_over_4yr" ${video.pricingTier === 'doc_long_over_4yr' ? 'selected' : ''}>Eldri enn 4 ár: 0 kr</option>
            `;
          }
        }
      }
    }

    editPricingTypeSelect.addEventListener('change', updateEditPricingFields);
    editCategorySelect.addEventListener('change', updateEditPricingFields);

    // Initialize pricing fields
    updateEditPricingFields();

    // Add role member handlers (director, screenwriter, producer, etc.)
    document.querySelectorAll('.edit-add-role-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const role = btn.dataset.role;
        const container = document.getElementById(`edit-${role}-container`);
        const idx = editRoleCounts[role];
        const div = document.createElement('div');
        div.className = 'name-input-group mt-sm';
        div.innerHTML = `
          <input type="text" id="edit-${role}${idx}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
          <input type="text" id="edit-${role}${idx}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
          <input type="text" id="edit-${role}${idx}LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
        `;
        container.appendChild(div);
        editRoleCounts[role]++;
      });
    });

    // Add crew member handler
    let editCrewCount = (video.keyCrew || []).length || 1;
    const editAddCrewBtn = document.getElementById('edit-add-crew');
    if (editAddCrewBtn) {
      editAddCrewBtn.addEventListener('click', () => {
        const container = document.getElementById('edit-crew-container');
        const div = document.createElement('div');
        div.className = 'name-input-group mt-sm';
        div.innerHTML = `
          <select id="edit-crew${editCrewCount}Role" class="form-input" style="min-width: 200px;">
            <option value="">Vel leiklut...</option>
            <option value="production_designer">Production Designer / Framleiðslusniðgevi</option>
            <option value="costume_designer">Costume Designer / Búnasniðgevi</option>
            <option value="makeup_artist">Makeup Artist / Sminkulistafólk</option>
            <option value="sound_recordist">Sound Recordist / Ljóðmeistari</option>
            <option value="sound_designer">Sound Designer / Ljóðsniðgevi</option>
            <option value="gaffer">Gaffer / Ljósmeistari</option>
            <option value="key_grip">Key Grip / Høvuðsgrippur</option>
            <option value="colourist">Colourist / Litviðgeri</option>
          </select>
          <input type="text" id="edit-crew${editCrewCount}FirstName" class="form-input" placeholder="${t('placeholder_firstname')}">
          <input type="text" id="edit-crew${editCrewCount}MiddleName" class="form-input" placeholder="${t('placeholder_middlename')}">
          <input type="text" id="edit-crew${editCrewCount}LastName" class="form-input" placeholder="${t('placeholder_lastname')}">
        `;
        container.appendChild(div);
        editCrewCount++;
      });
    }

    // Screenings & Awards management
    let editScreeningCount = 0;
    const editScreeningsContainer = document.getElementById(
      'edit-screenings-container',
    );
    const editAddScreeningBtn = document.getElementById('edit-add-screening');

    function addEditScreeningEntry(data = {}) {
      if (editScreeningCount >= 6) return;
      const idx = editScreeningCount;
      const div = document.createElement('div');
      div.className = 'screening-entry';
      div.style.cssText =
        'padding: 12px; background: rgba(0, 119, 192, 0.05); border-radius: 8px; margin-bottom: 8px;';
      div.innerHTML = `
        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
          <input type="text" id="edit-screening${idx}Festival" class="form-input" placeholder="Filmsfestival" value="${escapeHtml(data.festival || '')}" style="flex: 2; min-width: 180px;">
          <input type="text" id="edit-screening${idx}Location" class="form-input" placeholder="Staður / Land" value="${escapeHtml(data.location || '')}" style="flex: 1; min-width: 120px;">
          <input type="number" id="edit-screening${idx}Year" class="form-input" placeholder="Ár" value="${data.year || ''}" style="width: 80px;">
          <input type="text" id="edit-screening${idx}Awards" class="form-input" placeholder="Virðislønir (valfrí)" value="${escapeHtml(data.awards || '')}" style="flex: 2; min-width: 180px;">
          <button type="button" class="btn btn-small btn-danger" onclick="this.parentElement.parentElement.remove()">✕</button>
        </div>
      `;
      editScreeningsContainer.appendChild(div);
      editScreeningCount++;
      if (editScreeningCount >= 6) editAddScreeningBtn.style.display = 'none';
    }

    editAddScreeningBtn.addEventListener('click', () =>
      addEditScreeningEntry(),
    );

    // Pre-populate existing screenings
    if (video.screenings && Array.isArray(video.screenings)) {
      video.screenings.forEach((s) => addEditScreeningEntry(s));
    }

    function getEditScreeningsArray() {
      const entries =
        editScreeningsContainer.querySelectorAll('.screening-entry');
      const screenings = [];
      entries.forEach((entry) => {
        const inputs = entry.querySelectorAll('input');
        const festival = inputs[0]?.value?.trim();
        const location = inputs[1]?.value?.trim();
        const year = parseInt(inputs[2]?.value, 10) || null;
        const awards = inputs[3]?.value?.trim();
        if (festival || location || awards) {
          screenings.push({
            festival: festival || '',
            location: location || '',
            year,
            awards: awards || '',
          });
        }
      });
      return screenings.length > 0 ? screenings : null;
    }

    // Form submission
    document
      .getElementById('edit-form')
      .addEventListener('submit', async (e) => {
        e.preventDefault();

        const parseNameList = (text) => {
          if (!text) return null;
          return text
            .split(',')
            .map((name) => {
              const parts = name.trim().split(' ');
              return {
                firstName: parts[0] || '',
                middleName:
                  parts.length > 2 ? parts.slice(1, -1).join(' ') : '',
                lastName: parts[parts.length - 1] || '',
              };
            })
            .filter((n) => n.firstName || n.lastName);
        };

        const _getNameInput = (prefix) => {
          const firstName =
            document.getElementById(`edit-${prefix}FirstName`)?.value || '';
          const middleName =
            document.getElementById(`edit-${prefix}MiddleName`)?.value || '';
          const lastName =
            document.getElementById(`edit-${prefix}LastName`)?.value || '';

          if (!firstName && !middleName && !lastName) return null;
          return { firstName, middleName, lastName };
        };

        const getRoleArray = (role) => {
          const count = editRoleCounts[role] || 1;
          const people = [];
          for (let i = 0; i < count; i++) {
            const firstName =
              document.getElementById(`edit-${role}${i}FirstName`)?.value || '';
            const middleName =
              document.getElementById(`edit-${role}${i}MiddleName`)?.value ||
              '';
            const lastName =
              document.getElementById(`edit-${role}${i}LastName`)?.value || '';
            if (firstName || middleName || lastName) {
              people.push({ firstName, middleName, lastName });
            }
          }
          return people.length > 0 ? people : null;
        };

        // Get crew count dynamically
        const getCrewCount = () => {
          let count = 0;
          while (
            document.getElementById(`edit-crew${count}Role`) ||
            document.getElementById(`edit-crew${count}FirstName`)
          ) {
            count++;
          }
          return count;
        };

        const updatedVideo = {
          videoId: video.videoId,
          fileName: video.fileName,
          state: video.state,
          published: video.published,
          uploadedAt:
            video.uploadedAt || video.createdAt || new Date().toISOString(),
          rightsTermsAccepted: video.rightsTermsAccepted || false,
          rightsTermsAcceptedAt: video.rightsTermsAcceptedAt,
          rightsTermsVersion: video.rightsTermsVersion,
          rightsTermsAcceptedByUserId: video.rightsTermsAcceptedByUserId,
          rightsTermsAcceptedByEmail: video.rightsTermsAcceptedByEmail,
          rightsTermsLanguage: video.rightsTermsLanguage,
          rightsTermsAcceptanceIp: video.rightsTermsAcceptanceIp,
          rightsTermsAcceptanceUserAgent: video.rightsTermsAcceptanceUserAgent,
          category: document.getElementById('edit-category').value,
          originalTitle: document.getElementById('edit-originalTitle').value,
          internationalTitle: document.getElementById('edit-internationalTitle')
            .value,
          loglineFo: document.getElementById('edit-loglineFo').value,
          loglineEn: document.getElementById('edit-loglineEn').value,
          synopsisFo: document.getElementById('edit-synopsisFo').value,
          synopsisEn: document.getElementById('edit-synopsisEn').value,
          posterUrl: document.getElementById('edit-posterUrl').value,
          stillUrl: document.getElementById('edit-stillUrl').value,

          pricingType: document.getElementById('edit-pricingType').value,
          pricingTier:
            document.getElementById('edit-pricingTier').value || null,
          customPrice: document.getElementById('edit-customPrice').value
            ? Math.round(
                parseFloat(document.getElementById('edit-customPrice').value) *
                  100,
              )
            : null,

          // Calculate rental price based on pricing type (same logic as upload form)
          rentalPrice: (() => {
            const pricingType =
              document.getElementById('edit-pricingType').value;
            const pricingTier =
              document.getElementById('edit-pricingTier').value;
            const customPrice = document.getElementById('edit-customPrice')
              .value
              ? parseFloat(document.getElementById('edit-customPrice').value)
              : null;

            if (pricingType === 'free') {
              return 0;
            } else if (pricingType === 'custom' && customPrice !== null) {
              return Math.round(customPrice * 100); // Convert DKK to cents/øre
            } else if (pricingType === 'standard' && pricingTier) {
              // Map pricing tier to price in cents/øre
              const tierPrices = {
                short_under_3mo: 2900, // DKK 29
                short_under_1yr: 1900, // DKK 19
                short_under_4yr: 700, // DKK 7
                short_over_4yr: 0,
                feature_under_2yr: 5900, // DKK 59
                feature_over_2yr: 3900, // DKK 39
                feature_over_4yr: 0,
                doc_short_under_1yr: 1900, // DKK 19
                doc_short_under_4yr: 700, // DKK 7
                doc_short_over_4yr: 0,
                doc_long_under_2yr: 5900, // DKK 59
                doc_long_under_4yr: 3900, // DKK 39
                doc_long_over_4yr: 0,
                tv_series: 2900, // DKK 29 per episode
              };
              return tierPrices[pricingTier] || 0;
            }
            return video.rentalPrice || 0; // Fallback to existing price
          })(),

          director: getRoleArray('director'),
          screenwriter: getRoleArray('screenwriter'),
          producer: getRoleArray('producer'),
          dop: getRoleArray('dop'),
          editor: getRoleArray('editor'),
          composer: getRoleArray('composer'),

          mainCast: parseNameList(
            document.getElementById('edit-mainCast').value,
          ),
          keyCrew: (() => {
            const crew = [];
            const crewCount = getCrewCount();
            for (let i = 0; i < crewCount; i++) {
              const firstName =
                document.getElementById(`edit-crew${i}FirstName`)?.value || '';
              const middleName =
                document.getElementById(`edit-crew${i}MiddleName`)?.value || '';
              const lastName =
                document.getElementById(`edit-crew${i}LastName`)?.value || '';
              const role =
                document.getElementById(`edit-crew${i}Role`)?.value || '';
              if (firstName || middleName || lastName || role) {
                crew.push({
                  firstName,
                  middleName,
                  lastName,
                  role: role || null,
                });
              }
            }
            return crew.length > 0 ? crew : null;
          })(),
          screenings: getEditScreeningsArray(),

          productionCompany: document.getElementById('edit-productionCompany')
            .value,
          coproductionCompany: document.getElementById(
            'edit-coproductionCompany',
          ).value,

          runtimeHours:
            parseInt(document.getElementById('edit-runtimeHours').value, 10) ||
            0,
          runtimeMinutes:
            parseInt(
              document.getElementById('edit-runtimeMinutes').value,
              10,
            ) || 0,
          language: document.getElementById('edit-language').value,
          subtitles: document.getElementById('edit-subtitles').value,
          productionYear:
            parseInt(
              document.getElementById('edit-productionYear').value,
              10,
            ) || new Date().getFullYear(),
          imdbLink: document.getElementById('edit-imdbLink').value,

          rentalDurationHours:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : parseInt(
                  document.getElementById('edit-rentalDuration').value,
                  10,
                ) || 48,

          bankName:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : document.getElementById('edit-bankName').value,
          bankRegistrationNo:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : document.getElementById('edit-bankRegistrationNo').value,
          bankAccountNo:
            document.getElementById('edit-pricingType').value === 'free'
              ? null
              : document.getElementById('edit-bankAccountNo').value,
          geoRestricted: document.getElementById('edit-geoRestricted').checked,
          ageRating: document.getElementById('edit-ageRating').value || null,
        };

        // Upload images if provided
        try {
          if (selectedPosterFile) {
            const result = await api.uploadImage(
              selectedPosterFile,
              'poster',
              videoId,
            );
            updatedVideo.posterUrl = result.url;
            if (result.imageId) updatedVideo.posterAssetId = result.imageId;
          }

          if (selectedStillFile) {
            const result = await api.uploadImage(
              selectedStillFile,
              'still',
              videoId,
            );
            updatedVideo.stillUrl = result.url;
            if (result.imageId) updatedVideo.stillAssetId = result.imageId;
          }
        } catch (imageError) {
          console.error('Image upload error:', imageError);
          await showModal(
            `Filmurin er dagførdur, men mynduppløga miseydnaðist:\n\n${imageError.message}`,
            'warning',
            'Partvíst eydnað',
          );
        }

        // Upload new subtitles if provided
        if (subtitlesToUpload.length > 0) {
          try {
            for (let i = 0; i < subtitlesToUpload.length; i++) {
              const subtitle = subtitlesToUpload[i];
              await api.uploadSubtitle(
                videoId,
                subtitle.file,
                subtitle.language,
                subtitle.label,
              );
            }
          } catch (subtitleError) {
            console.error('Subtitle upload error:', subtitleError);
            await showModal(
              `Filmurin er dagførdur, men undirtekstuppløga miseydnaðist:\n\n${subtitleError.message}`,
              'warning',
              'Partvíst eydnað',
            );
          }
        }

        // Save changes
        try {
          // Debug: Log what we're sending
          console.log('[EDIT] Saving video with pricing:', {
            pricingType: updatedVideo.pricingType,
            pricingTier: updatedVideo.pricingTier,
            customPrice: updatedVideo.customPrice,
            rentalPrice: updatedVideo.rentalPrice,
          });

          await api.updateVideo(videoId, updatedVideo);
          await showModal(t('msg_success'), 'success', 'Success');
          router.navigate('/manage');
        } catch (error) {
          console.error('Failed to save changes:', error);
          await showModal(
            `Broytt at goyma broytingar:\n\n${error.message}`,
            'error',
            'Goyming miseydnaðist',
          );
        }
      });

    // Check for incomplete uploads (resumable upload for video replacement)
    setTimeout(async () => {
      const savedProgress = api.getUploadProgress(videoId);

      if (
        savedProgress &&
        savedProgress.uploadedChunks.length < savedProgress.totalChunks
      ) {
        const percentage = Math.round(
          (savedProgress.uploadedChunks.length / savedProgress.totalChunks) *
            100,
        );
        const fileSizeMB = (savedProgress.fileSize / (1024 * 1024)).toFixed(2);
        const timeAgo = formatTimeAgo(savedProgress.lastUpdated);

        const message =
          `📤 Incomplete video replacement detected!\n\n` +
          `File: ${savedProgress.fileName}\n` +
          `Size: ${fileSizeMB} MB\n` +
          `Progress: ${percentage}%\n` +
          `Last updated: ${timeAgo}\n\n` +
          `Would you like to resume this upload?\n\n` +
          `Note: You'll need to select the same file again to continue.`;

        const resume = await showModal(message, 'warning', 'Resume Upload?');

        if (!resume) {
          api.clearUploadProgress(videoId);
          await showModal(
            'Previous upload cleared. You can start a new upload.',
            'info',
            'Cleared',
          );
        } else {
          await showModal(
            `To resume:\n\n` +
              `1. Select the same file: "${savedProgress.fileName}"\n` +
              `2. The upload will automatically continue from ${percentage}%.\n\n` +
              `Select the file in the "Replace Video File" section above.`,
            'info',
            'How to Resume',
          );
        }
      }
    }, 500); // Small delay to let page render first
  }
}

function formatTimeAgo(timestamp) {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  return 'Just now';
}
